import { useMemo, useState } from 'react';
import { useEntrance } from '../context/entrance-context';
import { ZONE_TO_COLOR_REGION, ENTRANCE_CATEGORY_COLORS, ENTRANCE_CATEGORY_LABELS } from '../data/entrance-types';
import { AREA_COLORS } from '@/components/tracker/data/area-colors';
import { ZONE_POSITIONS } from '../data/zone-layout';
import { DestinationPicker } from '../shared/destination-picker';
import { ZoneRewardPicker } from '../shared/zone-reward-picker';
import { ZoneRewardDisplay } from '../shared/zone-reward-display';
import type { EntranceCategory } from '../data/entrance-types';

export function EntranceListView() {
  const { state, trackEntrance, untrackEntrance, toggleZoneReward } = useEntrance();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [rewardPickerZone, setRewardPickerZone] = useState<string | null>(null);
  const [rewardPickerRect, setRewardPickerRect] = useState<{ top: number; left: number; bottom: number } | null>(null);

  const grouped = useMemo(() => {
    const groups = new Map<string, typeof state.connections[string][]>();

    for (const conn of Object.values(state.connections)) {
      if (!state.visibleCategories.includes(conn.category)) continue;
      if (!state.visibleZones.includes(conn.zone)) continue;
      if (state.hideDone && conn.actualDestination === '✓ Done') continue;
      if (state.searchTerm) {
        const term = state.searchTerm.toLowerCase();
        const matches =
          conn.label.toLowerCase().includes(term) ||
          conn.zone.toLowerCase().includes(term) ||
          (conn.actualDestination?.toLowerCase().includes(term) ?? false);
        if (!matches) continue;
      }

      const list = groups.get(conn.zone) ?? [];
      list.push(conn);
      groups.set(conn.zone, list);
    }

    return groups;
  }, [state.connections, state.visibleCategories, state.visibleZones, state.hideDone, state.searchTerm]);

  return (
    <div className="h-full overflow-auto p-3">
      {Array.from(grouped.entries()).map(([zone, entrances]) => {
        const colorRegion = ZONE_TO_COLOR_REGION[zone] ?? 'Hyrule';
        const color = AREA_COLORS[colorRegion] ?? '#6b7280';
        const zoneLabel = ZONE_POSITIONS[zone]?.label ?? zone;

        return (
          <div key={zone} className="mb-4">
            <div
              className="text-xs font-bold px-2 py-1 rounded-t border-b-2 mb-1 flex items-center justify-between cursor-pointer hover:brightness-110"
              style={{ color, borderColor: color, backgroundColor: `${color}10` }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setRewardPickerRect({ top: rect.top, left: rect.left, bottom: rect.bottom });
                setRewardPickerZone(rewardPickerZone === zone ? null : zone);
              }}
            >
              <span>{zoneLabel} ({entrances.filter(e => e.isTracked).length}/{entrances.length})</span>
              <ZoneRewardDisplay rewards={state.zoneRewards[zone] ?? []} />
            </div>
            {rewardPickerZone === zone && rewardPickerRect && (
              <ZoneRewardPicker
                zone={zone}
                zoneRewards={state.zoneRewards}
                onToggle={toggleZoneReward}
                onClose={() => setRewardPickerZone(null)}
                anchorRect={rewardPickerRect}
              />
            )}
            <table className="w-full text-[11px]">
              <tbody>
                {entrances.map((conn) => {
                  const catColor = ENTRANCE_CATEGORY_COLORS[conn.category];
                  return (
                    <tr
                      key={conn.id}
                      className="border-b border-gray-800 hover:bg-gray-800/50"
                    >
                      <td className="py-1 px-2 w-4">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: catColor }}
                          title={ENTRANCE_CATEGORY_LABELS[conn.category]}
                        />
                      </td>
                      <td className="py-1 px-2 text-gray-300 w-1/3">
                        {conn.label}
                      </td>
                      <td className="py-1 px-2 text-gray-500">→</td>
                      <td className="py-1 px-2 relative">
                        {editingId === conn.id ? (
                          <DestinationPicker
                            currentValue={conn.actualDestination}
                            onSelect={(dest, from) => {
                              trackEntrance(conn.id, dest, from);
                              setEditingId(null);
                            }}
                            onClear={() => {
                              untrackEntrance(conn.id);
                              setEditingId(null);
                            }}
                            onClose={() => setEditingId(null)}
                          />
                        ) : (
                          <button
                            className={`text-left w-full ${
                              conn.isTracked
                                ? 'text-white'
                                : 'text-gray-600 italic'
                            }`}
                            onClick={() => setEditingId(conn.id)}
                          >
                            {conn.isTracked
                              ? conn.actualDestination
                              : '? (click to set)'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
